/**
 * Incremental reader over the columnar Arrow store, for consumers that want ROWS rather than columns.
 *
 * WHY THIS EXISTS. `arrowStreamClient` exposes no per-frame callback — decode goes straight into
 * `liveColumnarStore` (`onBinary` -> `tableToFrame` -> `appendFrame`, all private). A row-oriented
 * consumer like the live timeseries buffer therefore cannot subscribe to frames; it has to read the
 * store. Reading the WHOLE window every tick is what the live page used to do, and it re-materializes
 * and re-sorts every series on every animation frame — throwing away the zero-alloc typed-array work
 * the ring does upstream.
 *
 * So this keeps a per-source cursor and returns only what arrived since the last drain. The first
 * drain for a source reads back to `coldStartMs` instead, which is how the server's
 * subscribe-snapshot (the "load the window first" step) enters the buffer — no special case needed.
 *
 * IDEMPOTENCE. Overlapping re-reads are harmless: `liveTimeseriesBuffer.appendRows` keeps only points
 * strictly newer than what it already holds, and the store's own `appendFrame` drops non-newer
 * timestamps. That is what makes a cursor-based read safe against a re-sent snapshot on reconnect.
 *
 * DRAIN ONCE PER SOURCE. The cursor advances on every call, so a second drain in the same tick
 * returns `[]`. Callers with several series on one boat must drain once and fan the SAME array out to
 * each series — see `liveColumnarTailReader.test.ts`, which pins this.
 */

import { liveColumnarStore } from "../store/liveColumnarStore";

export interface ColumnarTailReader {
  /**
   * Rows for one FRAME source strictly newer than this reader's cursor, ascending; advances the
   * cursor to the last row returned. On the first call for a source (cold cursor) reads back to
   * `coldStartMs` instead.
   */
  drain(frameSourceId: number, coldStartMs: number): Record<string, unknown>[];
  /** Newest timestamp this reader has handed out, or null before the first row. */
  latestTs(): number | null;
  /** Forget cursors so the next drain re-reads from `coldStartMs` (reconnect / re-selection). */
  reset(frameSourceId?: number): void;
}

export function createColumnarTailReader(): ColumnarTailReader {
  /** frame source id -> timestamp of the last row handed out. */
  const cursors = new Map<number, number>();
  let latest: number | null = null;

  return {
    drain(frameSourceId, coldStartMs) {
      const edge = liveColumnarStore.latestTs(new Set([frameSourceId]));
      if (edge == null) {return [];}

      const cursor = cursors.get(frameSourceId);
      // +1 so the cursor row itself is excluded — windowRows is inclusive at both ends.
      const from = cursor == null ? coldStartMs : cursor + 1;
      if (from > edge) {return [];}

      const rows = liveColumnarStore.windowRows(frameSourceId, from, edge) as Record<string, unknown>[];
      if (rows.length === 0) {
        // Nothing in range, but the source HAS data: pin the cursor to the edge so a cold reader
        // whose window starts after the newest sample does not re-scan the ring every tick.
        if (cursor == null) {cursors.set(frameSourceId, edge);}
        return [];
      }

      const lastTs = Number(rows[rows.length - 1].timestamp);
      if (Number.isFinite(lastTs)) {
        cursors.set(frameSourceId, lastTs);
        if (latest == null || lastTs > latest) {latest = lastTs;}
      }
      return rows;
    },

    latestTs() {
      return latest;
    },

    reset(frameSourceId) {
      if (frameSourceId === undefined) {
        cursors.clear();
        latest = null;
        return;
      }
      cursors.delete(frameSourceId);
    },
  };
}
